import React, { useState, useEffect } from 'react';
import { AttributeOption, AttributeSelectorProps } from './characterTypes';
import {
  AttributeSectionContainer,
  AttributeTitle,
  OptionsContainer,
  OptionButton,
  RandomizeAttributeButton,
  ShowMoreButton,
  CustomInputContainer,
} from './CharacterStyles';
import SingleLineInputField from '../../../features/ChatPage/SingleLineInputField';
import { iconMapping } from './charactersUtils';
import { getIsOnDesktop } from '../../../styleHelpers';


const AttributeSelector: React.FC<AttributeSelectorProps> = ({
  options,
  selectedValue,
  onChange,
  title,
  attributeKey,
  multiSelect = false,
  onRandomize,
  allowCustomInput,
}) => {
  // Initialize showCustomInput based on whether the selectedValue is not an option ID
  const isCustomValue =
    allowCustomInput &&
    typeof selectedValue === 'string' &&
    !options.some((option) => option.id === selectedValue);

  const [showCustomInput, setShowCustomInput] = useState(!!isCustomValue);
  const [customValue, setCustomValue] = useState(
    isCustomValue ? (selectedValue as string) : '',
  );

  // Add useEffect to handle navigation and state restoration
  useEffect(() => {
    if (allowCustomInput && typeof selectedValue === 'string') {
      const isCustomValue = !options.some(
        (option) => option.id === selectedValue,
      );
      setShowCustomInput(isCustomValue);
      if (isCustomValue) {
        setCustomValue(selectedValue);
      }
    }
  }, [selectedValue, options, allowCustomInput]);

  const handleSelect = (optionId: string) => {
    if (optionId === 'custom' && allowCustomInput) {
      setShowCustomInput(true);
      return;
    }

    if (multiSelect) {
      // Handle multi-select case (arrays)
      const currentValues = (selectedValue as string[]) || [];
      if (currentValues.includes(optionId)) {
        // Remove if already selected
        onChange(currentValues.filter((id) => id !== optionId));
      } else {
        // Add if not selected
        onChange([...currentValues, optionId]);
      }
    } else {
      // Handle single-select case (strings)
      onChange(optionId);
    }
  };

  const handleCustomInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCustomValue(value);
    onChange(value);
  };

  const isSelected = (optionId: string) => {
    if (optionId === 'custom') {
      return showCustomInput;
    }

    if (multiSelect) {
      return ((selectedValue as string[]) || []).includes(optionId);
    }

    // If it's a custom value (not matching any option ID),
    // don't highlight any option button
    if (
      typeof selectedValue === 'string' &&
      !options.some((option) => option.id === selectedValue) &&
      optionId !== 'custom'
    ) {
      return false;
    }

    return selectedValue === optionId;
  };

  const Icon = iconMapping[attributeKey] || null;
  return (
    <AttributeSectionContainer>
      <AttributeTitle>
        {Icon && <Icon key={attributeKey} size={16} />}
        {title}
        <RandomizeAttributeButton
          onClick={() => onRandomize(attributeKey)}
          title={`Randomize ${title}`}
        >
          🎲 Random
        </RandomizeAttributeButton>
      </AttributeTitle>

      <Options
        options={options}
        isSelected={isSelected}
        handleSelect={handleSelect}
        allowCustomInput={allowCustomInput || false}
        showCustomInput={showCustomInput}
        setShowCustomInput={setShowCustomInput}
      />

      {showCustomInput && (
        <CustomInputContainer>
          <SingleLineInputField
            type="text"
            value={customValue}
            onChange={handleCustomInputChange}
            placeholder="Enter custom..."
            autoFocus
            hasBorder={true}
          />
        </CustomInputContainer>
      )}
    </AttributeSectionContainer>
  );
};

interface OptionsProps {
  options: AttributeOption[];
  isSelected: (optionId: string) => boolean | undefined;
  handleSelect: (optionId: string) => void;
  allowCustomInput: boolean;
  showCustomInput: boolean;
  setShowCustomInput: (show: boolean) => void;
}

const Options = ({
  options,
  isSelected,
  handleSelect,
  allowCustomInput,
  showCustomInput,
  setShowCustomInput,
}: OptionsProps) => {
  const HOW_MANY_TO_SHOW_BY_DEFAULT = 4;
  const isOnDesktop = getIsOnDesktop();
  const [showAllOptions, setShowAllOptions] = React.useState(isOnDesktop);
  const hasMoreOptions = options.length > HOW_MANY_TO_SHOW_BY_DEFAULT;

  const visibleOptions = showAllOptions
    ? options
    : options.slice(0, HOW_MANY_TO_SHOW_BY_DEFAULT);

  useEffect(() => {
    // Show all options on desktop
    if (isOnDesktop) {
      setShowAllOptions(true);
      return;
    }
    
    // On mobile, check if any selected option is beyond the default visible limit
    const isSelectedOptionBeyondDefault = options
      .slice(HOW_MANY_TO_SHOW_BY_DEFAULT)
      .some((option) => isSelected(option.id));
      
    if (isSelectedOptionBeyondDefault) {
      setShowAllOptions(true);
    }
  }, [options, isSelected, HOW_MANY_TO_SHOW_BY_DEFAULT, isOnDesktop]);

  return (
    <OptionsContainer>
      {visibleOptions.map((option) => (
        <OptionButton
          key={option.id}
          isSelected={isSelected(option.id) || false}
          onClick={() => handleSelect(option.id)}
        >
          {option.label}
        </OptionButton>
      ))}

      {hasMoreOptions && !showAllOptions && (
        <ShowMoreButton onClick={() => setShowAllOptions(true)}>
          Show more...
        </ShowMoreButton>
      )}

      {allowCustomInput && (
        <OptionButton
          onClick={() => setShowCustomInput(true)}
          key="custom"
          isSelected={showCustomInput}
        >
          Custom...
        </OptionButton>
      )}
    </OptionsContainer>
  );
};

export default AttributeSelector;
